import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, Trash2, User, LogOut, LayoutDashboard, Box, FolderOpen, ClipboardList, Upload, FileSpreadsheet, Calculator, MapPin, Clock, Check, AlertCircle, Gift, ChevronDown, HelpCircle, Edit, Save, Image, Settings, Ruler, X, Menu } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../config";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";

const Logo = ({ size = "default" }) => (
  <Link to="/" className="flex items-center">
    <img
      src="https://customer-assets.emergentagent.com/job_ukok-trade-hub/artifacts/lytbo5x7_Gemini_Generated_Image_9eoaym9eoaym9eoa.jpeg"
      alt="UKOK"
      className={size === "small" ? "h-10" : "h-12 md:h-14"}
    />
  </Link>
);

// Admin Login
const AdminLogin = () => {
  const { login, register, admin } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (admin) navigate("/admin/dashboard");
  }, [admin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (isRegister) {
        await register(form.email, form.password, form.name);
      } else {
        await login(form.email, form.password);
      }
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5] py-12 px-4" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo />
          <h1 className="text-3xl font-black uppercase tracking-tight mt-6">Admin Portal</h1>
        </div>
        <div className="bg-white border-2 border-[#0A0A0A] p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="font-bold">Name</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="admin-name"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="font-bold">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="mt-1"
                data-testid="admin-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-bold">Password</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="mt-1"
                data-testid="admin-password"
              />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <Button type="submit" className="btn-primary w-full h-12" disabled={loading} data-testid="admin-submit">
              {loading ? "Please wait..." : isRegister ? "Create Account" : "Sign In"}
            </Button>
          </form>
          <button
            onClick={() => setIsRegister(!isRegister)}
            className="w-full text-center text-sm text-gray-500 mt-4 hover:text-[#FF6B00]"
          >
            {isRegister ? "Already have an account? Sign in" : "Need an account? Register"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Admin Layout
const AdminLayout = ({ children }) => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!admin) navigate("/admin");
  }, [admin, navigate]);

  if (!admin) return null;

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: Box, label: "Products", path: "/admin/products" },
    { icon: FolderOpen, label: "Categories", path: "/admin/categories" },
    { icon: ClipboardList, label: "Orders", path: "/admin/orders" },
    { icon: Gift, label: "Coupons", path: "/admin/coupons" },
    { icon: HelpCircle, label: "FAQs", path: "/admin/faqs" },
    { icon: Edit, label: "Content & SEO", path: "/admin/content" },
  ];

  return (
    <div className="flex min-h-screen" data-testid="admin-layout">
      {/* Mobile Sidebar Toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 bg-[#0A0A0A] text-white p-2"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X /> : <Menu />}
      </button>

      {/* Sidebar */}
      <aside className={`admin-sidebar w-64 fixed lg:static inset-y-0 left-0 z-40 transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform`}>
        <div className="p-6">
          <Logo size="small" />
        </div>
        <nav className="mt-6">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`admin-sidebar-link ${window.location.pathname === item.path ? "active" : ""}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="border-t border-gray-800 pt-4">
            <p className="text-sm text-gray-400 mb-2">{admin.name}</p>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-gray-400 hover:text-[#FF6B00]"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 bg-[#F5F5F5] min-h-screen lg:ml-0">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const { authAxios } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authAxios.get("/dashboard/stats")
      .then((res) => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [authAxios]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  const formatPrice = (price) => `₦${price.toLocaleString()}`;

  return (
    <AdminLayout>
      <h1 className="text-3xl font-black uppercase tracking-tight mb-8" data-testid="admin-dashboard-title">Dashboard</h1>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="border-2 border-[#0A0A0A]">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 uppercase tracking-wider">Total Products</p>
            <p className="text-3xl font-black">{stats?.total_products || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-2 border-[#0A0A0A]">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 uppercase tracking-wider">Total Orders</p>
            <p className="text-3xl font-black">{stats?.total_orders || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-2 border-[#0A0A0A]">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 uppercase tracking-wider">Revenue</p>
            <p className="text-3xl font-black text-[#FF6B00]">{formatPrice(stats?.total_revenue || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-2 border-[#0A0A0A]">
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 uppercase tracking-wider">Pending Orders</p>
            <p className="text-3xl font-black text-yellow-600">{stats?.pending_orders || 0}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <Card className="border-2 border-[#0A0A0A]">
          <CardHeader>
            <CardTitle className="font-black uppercase">Recent Orders</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_orders?.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_orders.map((order) => (
                  <div key={order.id} className="flex justify-between items-center py-2 border-b border-[#E5E5E5]">
                    <div>
                      <p className="font-bold">{order.order_number}</p>
                      <p className="text-sm text-gray-500">{order.customer_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatPrice(order.total)}</p>
                      <span className={`badge-${order.status}`}>{order.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No orders yet</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-2 border-[#0A0A0A]">
          <CardHeader>
            <CardTitle className="font-black uppercase">Quick Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between py-2 border-b border-[#E5E5E5]">
                <span>Categories</span>
                <span className="font-bold">{stats?.total_categories || 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#E5E5E5]">
                <span>Paid Orders</span>
                <span className="font-bold text-green-600">{stats?.paid_orders || 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#E5E5E5]">
                <span>Low Stock Products</span>
                <span className="font-bold text-red-600">{stats?.low_stock_products || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};


export { AdminLogin, AdminLayout, AdminDashboard };
